import type { GameConfig, PrizeConfig } from "../types/index.js";

/** Default configuration values (overridable via server .env, see CONFIGURATION docs). */
export const DEFAULT_CONFIG: GameConfig = {
  requiredPlayers: 4,
  targetHp: 3,
  missionTimeLimitSeconds: 180,
  ceremonyDelayMs: 8000,
  playerDisconnectTimeoutMs: 15000,
  attackCooldownMs: 600,
  attackRange: 3.5,
};

/** Lucky-draw subsystem defaults — off until an admin explicitly enables it. */
export const DEFAULT_PRIZE_CONFIG: PrizeConfig = {
  enabled: false,
  intervalSeconds: 300,
  totalPrizes: 3,
};

/** Number of ping/pong round trips used to estimate clock offset on join. */
export const CLOCK_SYNC_SAMPLE_COUNT = 8;

/** Interval (ms) for ongoing background clock re-sync while connected. */
export const CLOCK_SYNC_INTERVAL_MS = 15000;

export interface ClockSample {
  offset: number;
  rtt: number;
}

/**
 * Given a set of ping/pong round-trip samples, estimate the clock offset
 * (serverTime - clientTime) using the sample(s) with lowest RTT, which are
 * least distorted by network jitter (classic NTP-style estimation).
 */
export function estimateClockOffset(samples: ClockSample[]): number {
  if (samples.length === 0) return 0;
  const sorted = [...samples].sort((a, b) => a.rtt - b.rtt);
  const best = sorted.slice(0, Math.max(1, Math.ceil(sorted.length / 2)));
  const sum = best.reduce((acc, s) => acc + s.offset, 0);
  return sum / best.length;
}

/**
 * Compute a single clock sample from a ping/pong exchange.
 * t0 = client send time, t1 = server receive/send time, t2 = client receive time.
 * Assumes symmetric network latency.
 */
export function computeSample(t0: number, t1: number, t2: number): ClockSample {
  const rtt = t2 - t0;
  const offset = t1 - (t0 + rtt / 2);
  return { offset, rtt };
}
